/**
 * Product Card Component
 * 
 * Handles interactive functionality for product cards:
 * - Quick add to cart
 * - Variant selection
 * - Image hover effects
 */

import { addToCart } from '../utils/cart.js';
import FlipButton from '../snippets/flipbutton';

export default class ProductCard {
  constructor(element) {
    this.element = element;
    this.quickAddBtn = element.querySelector('[data-quick-add]');
    this.variantSelect = element.querySelector('[data-variant-select]');
    this.form = element.querySelector('form');
    this.flipbuttons = this.element.querySelectorAll('.js-flipbutton');
    this.init();
  }

  init() {
    // console.log('🛍️ Product card initialized');

    // Quick add button
    if (this.quickAddBtn) {
      this.quickAddBtn.addEventListener('click', this.handleQuickAdd.bind(this));
    }

    // Variant selection
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.handleVariantChange.bind(this));
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    this.flipbuttons.forEach((element) => new FlipButton(element, { button: element.parentNode, fade: false }));
  }

  /**
   * Handle quick add to cart
   */
  async handleQuickAdd(e) {
    e.preventDefault();

    const variantId = this.element.dataset.variantId;
    const quantity = 1;

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    try {
      this.setLoading(true);

      await addToCart(variantId, quantity);

      this.setLoading(false);
      this.showSuccess();

      // Dispatch custom event for cart updates (using cart:add which updates count)
      document.dispatchEvent(new CustomEvent('cart:add'));

      // Open cart drawer to show the item
      setTimeout(() => {
        const cartDrawer = document.querySelector('.cart-drawer');
        if (cartDrawer && window.cartDrawerInstance) {
          window.cartDrawerInstance.open();
        }
      }, 500); // Small delay to let the success message show

    } catch (error) {
      console.error('Failed to add to cart:', error);
      this.setLoading(false);
      this.showError();
    }
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(e) {
    const selectedVariantId = e.target.value;

    // Update product card data
    this.element.dataset.variantId = selectedVariantId;

    // Update price, availability, etc.
    this.updateProductInfo(selectedVariantId);
  }

  /**
   * Handle form submission for products with options
   */
  async handleFormSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;

    try {
      this.setLoading(true);

      await addToCart(variantId, quantity);

      this.setLoading(false);
      this.showSuccess();

      document.dispatchEvent(new CustomEvent('cart:add'));

    } catch (error) {
      console.error('Failed to add to cart:', error);
      this.setLoading(false);
      this.showError();
    }
  }

  /**
   * Update product information based on selected variant
   */
  updateProductInfo(variantId) {
    // Update price
    const priceElement = this.element.querySelector('[data-price]');
    const compareAtPriceElement = this.element.querySelector('[data-compare-price]');

    // This would typically fetch variant data from a JSON endpoint
    // or read from inline data attributes

    console.log(`Variant changed to: ${variantId}`);
  }

  /**
   * Set loading state
   */
  setLoading(isLoading) {
    if (isLoading) {
      this.element.classList.add('is-loading');
      if (this.quickAddBtn) {
        this.quickAddBtn.disabled = true;
        this.quickAddBtn.setAttribute('aria-busy', 'true');
      }
    } else {
      this.element.classList.remove('is-loading');
      if (this.quickAddBtn) {
        this.quickAddBtn.disabled = false;
        this.quickAddBtn.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Show success message
   */
  showSuccess() {
    this.element.classList.add('is-success');

    setTimeout(() => {
      this.element.classList.remove('is-success');
    }, 2000);
  }

  /**
   * Show error message
   */
  showError() {
    this.element.classList.add('is-error');

    setTimeout(() => {
      this.element.classList.remove('is-error');
    }, 2000);
  }
}

